import * as React from "react";
import {defineMessages, FormattedMessage, injectIntl} from "react-intl";
import {Link} from "react-router-dom";
import {ColumnsDescription, ColumnWidth} from "../common/ui/table/ControlRoomColumns";
import {createBasicTableComponent} from "../common/ui/table/ControlRoomTable";
import {connect} from "react-redux";
import {PagedTableConfig} from "../common/ui/table/PagedTable";
import {DateUtil} from "../common/util/DateUtil";
import {actions} from "./actions";
import {canRemoveService, Service, ServiceStatus} from "./model";
import {selectors} from "./selectors";
import {ServiceDeleteButton} from "./ServiceDeleteButton";
import {selectors as pagedDataSelectors} from "../common/util/pagination/PaginationUtil";
import {ServiceStartStopButton} from "./ServiceStartStopButton";
import {sortItems, SortSettings} from "../common/ui/table/model";
import {COLUMN_SORT_SETTINGS_SERVICES, localRepository} from "../common/LocalRepository";

const config: PagedTableConfig = {
  canMultiSelect: false,
  noItemsFoundMessage: <FormattedMessage id="studio.services.service-list.no-services-found"
                                         defaultMessage="No services found"/>,
};

let currentOnSortChange: ((colKey: string) => void) | null = null;

const makeHeader = (label: React.ReactNode, columnKey: string) => {
  let indicator = "";
  const sortSettings: SortSettings = localRepository.getOptions<SortSettings>(COLUMN_SORT_SETTINGS_SERVICES);
  if (sortSettings?.column === columnKey) {
    indicator = sortSettings.direction.startsWith("asc") ? " ▲" : " ▼";
  }

  return (
      <button
          type="button"
          onClick={() => currentOnSortChange && currentOnSortChange(columnKey)}
          style={{background: "none", border: "none", padding: 0, margin: 0, cursor: "pointer"}}
          aria-label={`Sort by ${String(columnKey)}`}
      >
        <div className={"columnLabel"} style={{display: "inline-flex"}}>
          {label}
          <div className={"sortIndicator"} id={`sortIndicator_${columnKey}`}>{indicator}</div>
        </div>
      </button>
  );
}
const MESSAGES = defineMessages({
  statusPendingTooltip: {
    id: "studio.services.service-list.status.tooltip-pending",
    defaultMessage: "Preprocessing job is running for the service {name}",
  },
  createdByUnknown: {
    id: "studio.services.service-list.created-by.unknown",
    defaultMessage: "Unknown",
  },
  startStopTooltip: {
    id: "studio.services.service-list.start-stop.tooltip",
    defaultMessage: "Start/Stop {name}",
  },
  canRemoveTooltip: {
    id: "studio.services.service-list.remove.can-remove-tooltip",
    defaultMessage: "Remove {title}",
  },
  cannotRemoveTooltip: {
    id: "studio.services.service-list.remove.cannot-remove-tooltip",
    defaultMessage: "Cannot remove {title}",
  },
});

const columns: ColumnsDescription<Service> = {
  title: {
    header: makeHeader(<FormattedMessage id="studio.services.service-list.title" defaultMessage="Title"/>, "title"),
    cellContent: (service) => <Link to={`/services/${service.id}`}>{service.title}</Link>,
    cellTooltip: (service) => service.title,
    columnWidth: ColumnWidth.flex(1),
  },
  type: {
    header: makeHeader(<FormattedMessage id="studio.services.service-list.type" defaultMessage="Type"/>, "type"),
    cellContent: (service) => service.type.toString().toUpperCase(),
    cellTooltip: (service) => service.type.toString().toUpperCase(),
    columnWidth: ColumnWidth.pixels(70),
  },
  status: {
    header: makeHeader(<FormattedMessage id="studio.services.service-list.status" defaultMessage="Status"/>, "status"),
    cellContent: (service) => {
      if (service.status === ServiceStatus.PENDING) {
        return <Link to={`/jobs/`} style={{fontWeight: "inherit"}}>{service.status}</Link>;
      }
      return service.status.toString();
    },
    cellTooltip: (service, intl) => {
      if (service.status === ServiceStatus.PENDING) {
        return intl.formatMessage(MESSAGES.statusPendingTooltip, {name: service.name});
      }
      return service.status.toString();
    },
    columnWidth: ColumnWidth.pixels(80),
  },
  createdBy: {
    header: makeHeader(<FormattedMessage id="studio.services.service-list.created-by" defaultMessage="Created by"/>,
        "createdBy"),
    cellContent: (service) => service.createdBy ? service.createdBy.username :
      <FormattedMessage id="studio.services.service-list.created-by.unknown" defaultMessage="Unknown"/>,
    cellTooltip: (service, intl) => service.createdBy ? service.createdBy.username : intl.formatMessage(MESSAGES.createdByUnknown),
    columnWidth: ColumnWidth.USERNAME_COLUMN_WIDTH,
  },
  creationTime: {
    header: makeHeader(<FormattedMessage id="studio.services.service-list.date-created" defaultMessage="Date created"/>,
        "creationTime"),
    cellContent: (service) => DateUtil.formatDateTime(service.creationTime),
    cellTooltip: (service) => DateUtil.formatDateTime(service.creationTime),
    columnWidth: ColumnWidth.DATETIME_COLUMN_WIDTH,
  },
  startStop: {
    header: <FormattedMessage id="studio.services.service-list.start-stop" defaultMessage="Start/Stop"/>,
    cellContent: (service) => <ServiceStartStopButton service={service} compact/>,
    cellTooltip: (service, intl) => intl.formatMessage(MESSAGES.startStopTooltip, {name: service.name}),
    columnWidth: ColumnWidth.STARTSTOP_BUTTON_COLUMN_WIDTH,
  },
  remove: {
    header: <FormattedMessage id="studio.services.service-list.remove" defaultMessage="Remove"/>,
    cellContent: (service) => <ServiceDeleteButton service={service} disabled={!canRemoveService(service)}/>,
    cellTooltip: (service, intl) => {
      if (canRemoveService(service)) {
        return intl.formatMessage(MESSAGES.canRemoveTooltip, {title: service.title});
      }
      return intl.formatMessage(MESSAGES.cannotRemoveTooltip, {title: service.title});
    },
    columnWidth: ColumnWidth.REMOVE_COLUMN_WIDTH,
  },
};

export const ServiceListComponent = createBasicTableComponent<Service>(columns);

function valueForColumn(item: Service, columnKey?: string | null) {
  switch (columnKey) {
  case "title":
    return (item.title || "").toString().toLowerCase();
  case "type":
    return (item.type || "").toString().toLowerCase();
  case "status":
    return (item.status || "").toString().toLowerCase();
  case "createdBy":
    return (item.createdBy?.username || "").toString().toLowerCase();
  case "creationTime":
    const t = (item as any).creationTime;
    const num = typeof t === "number" ? t : Date.parse(t);
    return Number.isNaN(num) ? 0 : num;
  default:
    return "";
  }
}

const ConnectedServiceList = connect(
    (state: any, ownProps: any) => {
      const pagedState = selectors.getPagedState(state) || ({} as any);
      const rawItems: Service[] = pagedDataSelectors.getAllLoadedData<Service>(pagedState) || [];
      const sorted = sortItems(rawItems, valueForColumn, ownProps?.sortSettings);
      return {
        isPaged: true,
        canMultiSelect: config.canMultiSelect,
        noItemsFoundMessage: config.noItemsFoundMessage,
        data: sorted,
        pageSize: pagedDataSelectors.getPageSize(pagedState),
        pages: pagedDataSelectors.getAllPages<Service>(pagedState),
        isAllDataLoaded: pagedDataSelectors.isAllPagesLoaded(pagedState),
      };
    },
    (dispatch: any) => ({
      loadPage: (pageNumber: number) => dispatch(actions.requestPage(pageNumber)),
    })
)(injectIntl(ServiceListComponent));

export const ServiceList = (props: any) => {
  currentOnSortChange = props.onSortChange || null;
  return <ConnectedServiceList {...props} />;
};
